import { FormattedMessage } from 'react-intl';
import React from 'react';
import bemify from 'bemify-js';

import Link from 'app/common/components/Link';

import BashCodeBlock from '../components/BashCodeBlock';
import Code from '../components/Code';
import Paragraph from '../components/Paragraph';
import StepTitle from '../components/StepTitle';
import SyntaxHighlighter from '../components/SyntaxHighlighter';

const bem = bemify('deploying');

const CODE_BLOCK = `$ zapier push
✔ Copying project to temp directory
✔ Installing project dependencies
✔ Applying entry point file
✔ Building app definition.json
✔ Validating project schema and style
✔ Zipping project and dependencies
✔ Testing build
✔ Cleaning up temp directory
✔ Uploading version 1.0.0
Push complete! Built build/build.zip and build/source.zip and uploaded them to Zapier.`;

const Deploying = () => (
  <div className={bem()}>
    <StepTitle>
      <FormattedMessage
        defaultMessage="Deploying Your App"
        id="headings-deploying-your-app"
      />
    </StepTitle>
    <Paragraph>
      <FormattedMessage
        defaultMessage="Alrighty then! Enough poking around in our local environment. Let's deploy it! Since this is your first time pushing your app version, you’ll need to register your app with Zapier. Run the"
        id="descriptions-register-app-with-zapier-prefix"
      />{' '}
      <Code>
        <FormattedMessage
          defaultMessage="zapier register"
          id="labels-command-zapier-register"
        />
      </Code>
      <FormattedMessage
        defaultMessage=" command and Zapier will ask you to provide a name and some details about your app."
        id="descriptions-register-app-with-zapier-suffix"
      />
    </Paragraph>
    <BashCodeBlock
      lines={[
        {
          content: 'zapier register',
          isCommand: true,
        },
      ]}
    />
    <Paragraph>
      <FormattedMessage
        defaultMessage="Follow the prompt and then you can push your app using"
        id="descriptions-push-app-after-registration-prefix"
      />{' '}
      <Code>
        <FormattedMessage
          defaultMessage="zapier push"
          id="labels-command-zapier-push"
        />
      </Code>
      <FormattedMessage
        defaultMessage=". Only you will be able to see the app until you invite testers."
        id="descriptions-push-app-after-registration-suffix"
      />
    </Paragraph>
    <BashCodeBlock
      lines={[
        {
          content: 'zapier push',
          isCommand: true,
        },
      ]}
    />
    <SyntaxHighlighter language="text">{CODE_BLOCK}</SyntaxHighlighter>
    <Paragraph>
      <FormattedMessage
        defaultMessage="Once you push your app, you'll be able to see it in the <component0>Zapier Platform UI</component0> ."
        id="descriptions-view-app-in-platform-ui"
        values={{
          component0: chunks => (
            <Link href="/" target="_blank">
              {chunks}
            </Link>
          ),
        }}
      />
    </Paragraph>
    <Paragraph>
      <FormattedMessage
        defaultMessage={
          'You can also now create Zaps with it! Pro tip - If the app doesn\'t show up in "Your Apps", search for the app in the Zap editor by typing the first few letters of what you named it.'
        }
        id="descriptions-create-zaps-and-find-app-tip"
      />
    </Paragraph>
    <Paragraph>
      <FormattedMessage
        defaultMessage="Your app will only be visible to you in the Zap editor. We'll talk about letting others use your app in the docs."
        id="descriptions-app-visible-only-to-you"
      />
    </Paragraph>
  </div>
);

export default Deploying;
